import { useState } from 'react';
import {
  Box, Container, Paper, Typography, ToggleButtonGroup, ToggleButton,
  Stack, Button, CircularProgress, Alert, Select, MenuItem, FormControl,
  InputLabel,
} from '@mui/material';
import {
  Assessment, WorkOutline, PieChart, FreeBreakfast, SwapHoriz,
  PictureAsPdf, TableChart,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { WorkloadReport } from '@/components/reports/WorkloadReport';
import { SubjectAllocationReport } from '@/components/reports/SubjectAllocationReport';
import { FreePeriodReport } from '@/components/reports/FreePeriodReport';
import { SubstitutionStatsReport } from '@/components/reports/SubstitutionStatsReport';
import { generateReportExcel, generateReportPDF, generateReportFilename, downloadBlob } from '@timetable/utils';
import {
  getTeacherWorkloadDirect, getSubjectAllocationDirect,
  getTeacherFreePeriodsDirectReport, getSubstitutionStatsDirect,
  getAcademicYearsDirect, getTimetableVersionsDirect,
  type TimetableVersion,
} from '@timetable/api';

type ReportType = 'workload' | 'subject-allocation' | 'free-periods' | 'substitution-stats';

/** Format a version's date range for display in the dropdown */
function formatVersion(v: TimetableVersion, index: number, total: number): string {
  const from = format(new Date(v.valid_from), 'MMM d, yyyy');
  if (!v.valid_to) return `${from} – present (current)`;
  const to = format(new Date(v.valid_to), 'MMM d, yyyy');
  return `${from} – ${to}`;
}

export function ReportsPage() {
  const [reportType, setReportType] = useState<ReportType>('workload');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedVersion, setSelectedVersion] = useState<string>(''); // valid_from date string
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const isVersionedReport = reportType !== 'substitution-stats';

  // Fetch academic years for the selector
  const { data: academicYears = [] } = useQuery({
    queryKey: ['academic-years'],
    queryFn: () => getAcademicYearsDirect(),
  });

  // Fetch timetable versions when a year is selected
  const { data: versions = [] } = useQuery({
    queryKey: ['timetable-versions', selectedYear],
    queryFn: () => getTimetableVersionsDirect(selectedYear),
    enabled: !!selectedYear,
  });

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    setSelectedVersion('');
  };

  const getReportTitle = () => {
    switch (reportType) {
      case 'workload': return 'Teacher Workload Report';
      case 'subject-allocation': return 'Subject Allocation Report';
      case 'free-periods': return 'Free Period Report';
      case 'substitution-stats': return 'Substitution Statistics Report';
    }
  };

  const getDateRangeString = () => {
    if (isVersionedReport) {
      if (!selectedYear || !selectedVersion) return '';
      const v = versions.find(v => v.valid_from === selectedVersion);
      return v ? formatVersion(v, 0, 1) : selectedVersion;
    }
    if (!startDate || !endDate) return '';
    return `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`;
  };

  const getReportColumns = () => {
    switch (reportType) {
      case 'workload': return [
        { header: 'Teacher Name', key: 'teacher_name', width: 25 },
        { header: 'Total Periods', key: 'total_periods', width: 15 },
        { header: 'Free Periods', key: 'free_periods', width: 15 },
      ];
      case 'subject-allocation': return [
        { header: 'Subject Code', key: 'subject_code', width: 15 },
        { header: 'Subject Name', key: 'subject_name', width: 25 },
        { header: 'Total Periods', key: 'total_periods', width: 15 },
        { header: 'Percentage', key: 'percentage', width: 15 },
      ];
      case 'free-periods': return [
        { header: 'Teacher Name', key: 'teacher_name', width: 25 },
        { header: 'Total Free', key: 'total_free_periods', width: 12 },
        { header: 'Monday', key: 'monday_free', width: 10 },
        { header: 'Tuesday', key: 'tuesday_free', width: 10 },
        { header: 'Wednesday', key: 'wednesday_free', width: 10 },
        { header: 'Thursday', key: 'thursday_free', width: 10 },
        { header: 'Friday', key: 'friday_free', width: 10 },
      ];
      case 'substitution-stats': return [
        { header: 'Teacher Name', key: 'teacher_name', width: 25 },
        { header: 'Total Substitutions', key: 'total_substitutions', width: 18 },
        { header: 'Auto Assignments', key: 'auto_assignments', width: 18 },
        { header: 'Manual Assignments', key: 'manual_assignments', width: 18 },
        { header: 'Avg Response Time (min)', key: 'avg_response_time_minutes', width: 20 },
      ];
      default: return [];
    }
  };

  const fetchReportData = async () => {
    if (isVersionedReport) {
      switch (reportType) {
        case 'workload': return getTeacherWorkloadDirect(selectedYear, selectedVersion);
        case 'subject-allocation': return getSubjectAllocationDirect(selectedYear, selectedVersion);
        case 'free-periods': return getTeacherFreePeriodsDirectReport(selectedYear, selectedVersion);
      }
    }
    const start = startDate?.toISOString().split('T')[0];
    const end = endDate?.toISOString().split('T')[0];
    return getSubstitutionStatsDirect(start, end);
  };

  const canExport = isVersionedReport
    ? !!selectedYear && !!selectedVersion
    : !!startDate && !!endDate;

  const handleExport = async (type: 'pdf' | 'xlsx') => {
    setIsExporting(true);
    setExportError(null);
    try {
      const data = await fetchReportData();
      const blob = type === 'pdf'
        ? generateReportPDF({ title: getReportTitle(), schoolName: 'School', dateRange: getDateRangeString(), data, columns: getReportColumns() })
        : generateReportExcel({ title: getReportTitle(), schoolName: 'School', dateRange: getDateRangeString(), data, columns: getReportColumns() });
      downloadBlob(blob, generateReportFilename(reportType, type, getDateRangeString()));
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const renderReport = () => {
    switch (reportType) {
      case 'workload':
        return <WorkloadReport academicYear={selectedYear || null} versionValidFrom={selectedVersion || null} />;
      case 'subject-allocation':
        return <SubjectAllocationReport academicYear={selectedYear || null} versionValidFrom={selectedVersion || null} />;
      case 'free-periods':
        return <FreePeriodReport academicYear={selectedYear || null} versionValidFrom={selectedVersion || null} />;
      case 'substitution-stats':
        return <SubstitutionStatsReport startDate={startDate} endDate={endDate} />;
      default: return null;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
            Reports and Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View comprehensive reports on teacher workload, subject allocation, and substitution statistics
          </Typography>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={3}>
            {/* Report Type */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Select Report Type</Typography>
              <ToggleButtonGroup
                value={reportType}
                exclusive
                onChange={(_, value) => value && setReportType(value as ReportType)}
                fullWidth
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="workload"><WorkOutline sx={{ mr: 1 }} />Teacher Workload</ToggleButton>
                <ToggleButton value="subject-allocation"><PieChart sx={{ mr: 1 }} />Subject Allocation</ToggleButton>
                <ToggleButton value="free-periods"><FreeBreakfast sx={{ mr: 1 }} />Free Periods</ToggleButton>
                <ToggleButton value="substitution-stats"><SwapHoriz sx={{ mr: 1 }} />Substitution Stats</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Filters */}
            {isVersionedReport ? (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Academic Year &amp; Version</Typography>
                <Stack direction="row" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Academic Year</InputLabel>
                    <Select
                      value={selectedYear}
                      label="Academic Year"
                      onChange={e => handleYearChange(e.target.value)}
                    >
                      {academicYears.map(y => (
                        <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 260 }} disabled={!selectedYear || versions.length === 0}>
                    <InputLabel>Timetable Version</InputLabel>
                    <Select
                      value={selectedVersion}
                      label="Timetable Version"
                      onChange={e => setSelectedVersion(e.target.value)}
                    >
                      {versions.map((v, i) => (
                        <MenuItem key={v.valid_from} value={v.valid_from}>
                          {formatVersion(v, i, versions.length)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Date Range</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <DatePicker label="Start Date" value={startDate} onChange={setStartDate}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                  <Typography>to</Typography>
                  <DatePicker label="End Date" value={endDate} onChange={setEndDate}
                    minDate={startDate || undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                </Stack>
              </Box>
            )}

            {/* Export */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>Export Report</Typography>
              {exportError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
                  {exportError}
                </Alert>
              )}
              <Stack direction="row" spacing={2}>
                <Button variant="outlined"
                  startIcon={isExporting ? <CircularProgress size={20} /> : <PictureAsPdf />}
                  onClick={() => handleExport('pdf')} disabled={isExporting || !canExport}>
                  Export to PDF
                </Button>
                <Button variant="outlined"
                  startIcon={isExporting ? <CircularProgress size={20} /> : <TableChart />}
                  onClick={() => handleExport('xlsx')} disabled={isExporting || !canExport}>
                  Export to Excel
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>

        <Box>{renderReport()}</Box>
      </Container>
    </LocalizationProvider>
  );
}
